import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import type { Person } from '../../../users/entities/person.js';
import { toCanonicalMonthStart } from '../../../db/utils/jobTicketDates.js';
import type { JobTicketFormData, PersistedJobTicketDates } from '../../../db/utils/jobTicketValidator.js';

/** Everything the job ticket form needs of a person. */
export const JOB_TICKET_PERSON_SELECTION = {
  _id: 1,
  roles: { name: 1, contexts: 1, permissions: 1 },
  mitarbeiterDaten: {
    personalNr: 1,
    deutschlandTicketBestellt: 1,
    jobTicketKenntnis: 1,
    jobTicketBestaetigt: 1,
    jobTicketDatenweitergabe: 1,
    jobTicketExportiertAm: 1,
    jobTicketBestellDatum: 1,
    jobTicketKuendigungsDatum: 1,
  },
  profilDaten: {
    adresse: { streetAndNumber: 1, city: 1, zip: 1, country: 1 },
    stammdaten: {
      titel: 1,
      vorname: 1,
      nachname: 1,
      namenszusatz: 1,
      vorsatzWort: 1,
      geburtsDatum: 1,
      geschlecht: 1,
    },
  },
} as const;

export type JobTicketPerson = NarrowedEntity<Person, typeof JOB_TICKET_PERSON_SELECTION>;

/**
 * Flattens a person into the job ticket form document. The mapping itself stays on the entity
 * (`Person.getJobTicketWrapper()`, also used by the ticket CSV export); `toObject()` unwraps the
 * entity into the plain data the functional form fields speak.
 *
 * Month granular values are normalized to their canonical month start on the way in, so legacy
 * values written as Berlin midnight do not display as the previous month.
 */
export const buildJobTicketFormData = (person: JobTicketPerson): JobTicketFormData => {
  const jobTicketData = person.getJobTicketWrapper().toObject();

  return {
    ...jobTicketData,
    jobTicketBestellDatum: toCanonicalMonthStart(jobTicketData.jobTicketBestellDatum),
    jobTicketKuendigungsDatum: toCanonicalMonthStart(jobTicketData.jobTicketKuendigungsDatum),
  };
};

/**
 * The stored order/cancel months, used to skip validation of dates the user did not touch.
 *
 * Read through the same wrapper snapshot as the form document, so both sides compare like for like.
 */
export const getPersistedJobTicketDates = (person: JobTicketPerson): PersistedJobTicketDates => {
  const jobTicketData = person.getJobTicketWrapper().toObject();

  return {
    orderDate: toCanonicalMonthStart(jobTicketData.jobTicketBestellDatum),
    cancelDate: toCanonicalMonthStart(jobTicketData.jobTicketKuendigungsDatum),
  };
};
